import React, { useMemo } from "react";
import {
	Box,
	Stepper,
	Step,
	StepLabel,
	StepContent,
	Typography,
	Paper,
	Grid,
	Chip,
	IconButton,
	Stack,
	Divider,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const getStatusColor = (status) => {
	const normalized = status?.toUpperCase();
	if (normalized === "SUCCESS" || normalized === "COMPLETED") return "success";
	if (normalized === "IN_PROGRESS" || normalized === "PENDING" || normalized === "JOB_TRIGGERED") return "warning";
	if (normalized === "FAILED" || normalized === "ERROR" || normalized === "JOB_TRIGGER_FAILED") return "error";
	return "default";
};

const normalizeSteps = (job) => {
	const raw = job?.progerss ?? job?.progress ?? [];
	if (Array.isArray(raw)) return raw;
	if (typeof raw === "string") {
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

const getStepStatus = (status) => status?.toUpperCase() || "PENDING";

const getActiveStepIndex = (steps) => {
	if (!steps.length) return 0;

	for (let i = 0; i < steps.length; i++) {
		const status = getStepStatus(steps[i].status);
		if (status === "FAILED" || status === "ERROR") return i;
		if (status === "IN_PROGRESS" || status === "PENDING") return i;
	}

	return steps.length;
};

const formatTimestamp = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const StepStatusIcon = ({ status }) => {
	const normalized = getStepStatus(status);
	const iconSx = { fontSize: 20 };

	if (normalized === "SUCCESS" || normalized === "COMPLETED") {
		return <CheckCircleOutlineRoundedIcon sx={iconSx} color="success" />;
	}
	if (normalized === "FAILED" || normalized === "ERROR") {
		return <ErrorOutlineRoundedIcon sx={iconSx} color="error" />;
	}
	if (normalized === "IN_PROGRESS" || normalized === "PENDING") {
		return <HourglassEmptyRoundedIcon sx={iconSx} color="warning" />;
	}
	return <RadioButtonUncheckedRoundedIcon sx={iconSx} color="disabled" />;
};

const InfoCard = ({ icon, label, value }) => (
	<Paper
		elevation={0}
		sx={{
			p: 2,
			height: "100%",
			border: "1px solid",
			borderColor: "divider",
			borderRadius: 2,
			bgcolor: "background.paper",
		}}>
		<Stack direction="row" spacing={1.5} alignItems="flex-start">
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 36,
					height: 36,
					borderRadius: 1.5,
					bgcolor: "grey.50",
					color: "text.secondary",
					flexShrink: 0,
				}}>
				{icon}
			</Box>
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 500 }}>
					{label}
				</Typography>
				<Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, wordBreak: "break-word" }}>
					{value || "-"}
				</Typography>
			</Box>
		</Stack>
	</Paper>
);

const JobDetailStepper = ({ job, onBack }) => {
	const steps = useMemo(() => normalizeSteps(job), [job]);
	const activeStep = useMemo(() => getActiveStepIndex(steps), [steps]);
	const jobStatus = job?.status?.toUpperCase() || "UNKNOWN";

	const infoCards = [
		{
			key: "job_id",
			label: "Job ID",
			value: job?.job_id,
			icon: <WorkOutlineRoundedIcon fontSize="small" />,
		},
		{
			key: "file_name",
			label: "File Name",
			value: job?.file_name,
			icon: <InsertDriveFileOutlinedIcon fontSize="small" />,
		},
		{
			key: "job_type",
			label: "Type",
			value: job?.job_type,
			icon: <CategoryOutlinedIcon fontSize="small" />,
		},
		{
			key: "created_by",
			label: "Created By",
			value: job?.created_by,
			icon: <PersonOutlineRoundedIcon fontSize="small" />,
		},
		{
			key: "createdAt",
			label: "Execution Date",
			value: job?.createdAt ? formatTimestamp(job.createdAt) : "-",
			icon: <CalendarTodayOutlinedIcon fontSize="small" />,
		},
	];

	return (
		<Box sx={{ width: "100%" }}>
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
				{onBack && (
					<IconButton
						onClick={onBack}
						sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mt: 0.25 }}>
						<ArrowBackRoundedIcon fontSize="small" />
					</IconButton>
				)}
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography variant="h6" fontWeight={600} color="text.primary">
						Job Details
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						Track execution progress and review job metadata.
					</Typography>
				</Box>
				<Chip
					label={jobStatus}
					color={getStatusColor(jobStatus)}
					variant="outlined"
					size="small"
					sx={{ fontWeight: 600, borderRadius: "6px", flexShrink: 0 }}
				/>
			</Box>

			<Grid container spacing={2} sx={{ mb: 3 }}>
				{infoCards.map((card) => (
					<Grid item xs={12} sm={6} md={3} key={card.key}>
						<InfoCard icon={card.icon} label={card.label} value={card.value} />
					</Grid>
				))}
			</Grid>

			<Paper
				elevation={0}
				sx={{
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					overflow: "hidden",
					bgcolor: "background.paper",
				}}>
				<Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
					<Typography
						variant="subtitle2"
						fontWeight={600}
						color="text.secondary"
						sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
						Execution Timeline
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{steps.length
							? `${steps.length} step${steps.length === 1 ? "" : "s"} recorded`
							: "No progress steps recorded for this job yet"}
					</Typography>
				</Box>

				<Box sx={{ p: { xs: 2, md: 3 } }}>
					{steps.length === 0 ? (
						<Box
							sx={{
								py: 4,
								px: 2,
								textAlign: "center",
								border: "1px dashed",
								borderColor: "divider",
								borderRadius: 2,
								bgcolor: "grey.50",
							}}>
							<Typography variant="body2" color="text.secondary">
								Progress updates will appear here once the job starts processing.
							</Typography>
						</Box>
					) : (
						<Stepper activeStep={activeStep} orientation="vertical" nonLinear>
							{steps.map((step, index) => {
								const stepStatus = getStepStatus(step.status);
								const isFailed = stepStatus === "FAILED" || stepStatus === "ERROR";
								const isCompleted = stepStatus === "SUCCESS" || stepStatus === "COMPLETED";
								const isLast = index === steps.length - 1;

								return (
									<Step key={`${step.step || "step"}-${index}`} completed={isCompleted} expanded>
										<StepLabel
											error={isFailed}
											StepIconComponent={() => <StepStatusIcon status={step.status} />}
											sx={{
												"& .MuiStepLabel-label": {
													fontWeight: 600,
													fontSize: "0.9rem",
												},
											}}>
											<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
												<Typography variant="body2" fontWeight={600}>
													{step.step || `Step ${index + 1}`}
												</Typography>
												<Chip
													label={stepStatus}
													size="small"
													color={getStatusColor(stepStatus)}
													variant="outlined"
													sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, borderRadius: "6px" }}
												/>
											</Stack>
										</StepLabel>
										<StepContent>
											<Stack spacing={0.75} sx={{ pb: isLast ? 0 : 1.5, pt: 0.5 }}>
												<Typography variant="caption" color="text.secondary">
													{formatTimestamp(step.timestamp)}
												</Typography>
												{step.message && (
													<Typography variant="body2" color={isFailed ? "error.main" : "text.primary"}>
														{step.message}
													</Typography>
												)}
											</Stack>
											{!isLast && <Divider sx={{ mt: 1.5, mb: 0.5 }} />}
										</StepContent>
									</Step>
								);
							})}
						</Stepper>
					)}
				</Box>
			</Paper>
		</Box>
	);
};

export default JobDetailStepper;
